import { useEffect, useContext, useState, forwardRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import freeDomains from 'free-email-domains';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Controller } from 'react-hook-form';
import TrialDetails from '../../components/client/subscription/TrialDetails';
import SsoOptions from '../../components/client/account/SsoOptions';
import { Divider } from '@chakra-ui/react';
import {
  Heading,
  Input,
  Center,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  VStack,
  Button,
  Box,
  Link,
  Text,
  InputGroup,
  InputRightElement,
  useColorModeValue,
  useToast,
  Flex,
  Container,
  Image,
  Checkbox,
  Grid,
  HStack,
  Spinner,
  SimpleGrid,
  GridItem,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CardForm from '../../components/common/CardForm';
import {
  REGEX_CONTAINS_BOTH_LOWER_AND_UPPERCASE,
  REGEX_HAS_AT_LEAST_ONE_NUMBER,
  REGEX_HAS_SYMBOL,
  lengthInBetween,
  hasLowerAndUpperCase,
  hasSymbol,
  hasDigit,
} from '../../utils/validations';
import { CustomCheckIcon } from '../../theme/icons/CustomCheckIcon';
import { CustomExclamationMarkIcon } from '../../theme/icons/CustomExclamationMarkIcon';
import { CustomVisibilityIcon } from '../../theme/icons/CustomVisibilityIcon';
import { CustomVisibilityOffIcon } from '../../theme/icons/CustomVisibilityOffIcon';
import { FORM_PADDING_TOP } from '../../utils/consts';

import { HttpContext } from '../../context/HttpContext';
import { AuthContext } from '../../context/AuthContext';
import { setToken as storeToken } from '../../utils/tokenStore';
import { useOAuthPopup } from '../../hooks/useOAuthPopup';
import CustomToast from '../../common/CustomToast';

import runtimeEnv from '@mars/heroku-js-runtime-env';
import EmailConfirmationModal from '../../components/modals/EmailConfirmationModal';
import logoSrcDark from '../../assets/images/logo.png';
import { usePlanToken } from '../../hooks/usePlanToken';
import {
  captureTrackingParams,
  getTrackingParam,
  withTrackingParams,
} from '../../utils/trackingParams';

const env = runtimeEnv();

const CARDLESS_FREE_TRIAL_ENABLED = true;

function attachSignupAnalyticsFromSearchParams(accountData, searchParams) {
  const hubspotutk =
    searchParams.get('hubspotutk') || getTrackingParam('hubspotutk');
  const gclid = searchParams.get('gclid') || getTrackingParam('gclid');
  const patch = {
    ...(hubspotutk && { hubspotutk }),
    ...(gclid && { gclid }),
  };
  if (Object.keys(patch).length) {
    accountData.analytics = { ...accountData.analytics, ...patch };
  }
}

const ChakraPhoneInputField = forwardRef((props, ref) => (
  <Input
    ref={ref}
    bg="transparent"
    border="none"
    _hover={{
      background: 'transparent',
      border: 'none',
    }}
    _focus={{
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
    }}
    _active={{
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
    }}
    _focusVisible={{
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
    }}
    _autofill={{
      background: 'transparent',
      border: 'none',
      WebkitTextFillColor: '#252525',
      boxShadow: '0 0 0px 1000px transparent inset',
      WebkitBoxShadow: '0 0 0px 1000px transparent inset',
      transition: 'background-color 9999s ease-out 0s',
      caretColor: 'currentColor',
    }}
    sx={{
      '&:-webkit-autofill': {
        WebkitTextFillColor: '#252525',
        boxShadow: '0 0 0px 1000px transparent inset',
        WebkitBoxShadow: '0 0 0px 1000px transparent inset',
        transition: 'background-color 9999s ease-out 0s',
        caretColor: 'currentColor',
      },
      '&:-webkit-autofill:hover': {
        WebkitTextFillColor: '#252525',
        boxShadow: '0 0 0px 1000px transparent inset',
        WebkitBoxShadow: '0 0 0px 1000px transparent inset',
        transition: 'background-color 9999s ease-out 0s',
        caretColor: 'currentColor',
      },
      '&:-webkit-autofill:focus': {
        WebkitTextFillColor: '#252525',
        boxShadow: '0 0 0px 1000px transparent inset',
        WebkitBoxShadow: '0 0 0px 1000px transparent inset',
        transition: 'background-color 9999s ease-out 0s',
        caretColor: 'currentColor',
      },
      '&:autofill': {
        boxShadow: '0 0 0px 1000px transparent inset',
      },
    }}
    {...props}
  />
));

const SignUpPage = () => {
  const { publicAxios, authAxios } = useContext(HttpContext);
  const { setAuthState, parseJwt } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const inviteToken = queryParams.get('inviteToken');

  useEffect(() => {
    captureTrackingParams();
  }, [location.search]);
  const { planToken } = usePlanToken();
  const [invitedAccount, setInvitedAccount] = useState(null);
  const [isTeamMemberInvitation, setIsTeamMemberInvitation] = useState(false);
  const [organisationName, setOrganisationName] = useState('');
  const [loading, setLoading] = useState(true);
  // const [plansLoading, setPlansLoading] = useState(true);
  // const [selectedPlan, setSelectedPlan] = useState(null);
  // const [viewPlans, setViewPlans] = useState(true);
  // const [plans, setPlans] = useState([]);
  // const [error, setError] = useState(null);

  const [show, setShow] = useState(false);
  const showPasswordClickHandler = () => setShow(!show);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required('Please enter your first name'),
    lastName: Yup.string().required('Please enter your last name'),
    companyName: isTeamMemberInvitation
      ? Yup.string()
      : inviteToken
        ? Yup.string()
        : Yup.string().required('Please enter your company name'),
    email: inviteToken
      ? Yup.string()
      : Yup.string()
          .required('Please enter your email address')
          .email('Please enter a valid email address')
          .test(
            'is-company-email',
            'Please use your company/work email address',
            (val) => {
              if (!val) return false;
              return !freeDomains.includes(val.split('@')[1]);
            }
          ),
    phone: Yup.string()
      .nullable()
      .transform((v) => (v?.trim() === '' ? null : v))
      .test('is-phone', 'Please enter a valid phone number', (val) =>
        val == null ? true : isValidPhoneNumber(val)
      ),
    plainPassword: Yup.string()
      .required('Please enter your password')
      .min(6, 'Please enter at least 6 characters')
      .max(30, 'Please enter 30 characters or less')
      .matches(REGEX_CONTAINS_BOTH_LOWER_AND_UPPERCASE)
      .matches(REGEX_HAS_AT_LEAST_ONE_NUMBER)
      .matches(REGEX_HAS_SYMBOL),
    passwordConfirmation: Yup.string()
      .required('Please confirm your password')
      .oneOf([Yup.ref('plainPassword'), null], 'Passwords must match'),
    termsAndCond: Yup.boolean().isTrue(),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
    watch,
    getFieldState,
    reset,
    control,
  } = useForm(formOptions);

  const watchPassword = watch('plainPassword');
  const watchTermsAndCond = watch('termsAndCond');
  // const watchNewsletter = watch('newsletter');
  const fieldStatePassword = getFieldState('plainPassword');
  const toast = useToast();
  const modeIconColor = useColorModeValue('secondaryDark06', '#DDDDDD');

  // const handleSelectPlan = (plan) => {
  //   const queryPlanToken = queryParams.get('planToken');

  //   if (!queryPlanToken) {
  //     setSelectedPlan(plan);

  //     const token = plan.stripeProductId.replace(/^prod_/, '');
  //     queryParams.set('planToken', token);
  //     navigate(`?${queryParams.toString()}`, { replace: true });
  //   }
  // };
  // const findMatchingPrice = (prices) => {
  //   return prices.find((p) => p.interval === 'month');
  // };

  const handleOAuthSuccess = async (token, refreshToken) => {
    const exp = parseJwt(token).exp;
    storeToken(token, exp);

    try {
      const { data } = await authAxios.get(`accounts/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const account = data.account || null;

      if (account) {
        setAuthState({ token, account });
      }

      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Error fetching account data:', error);
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Failed to complete sign in. Please try again."
            description=""
            onClose={props.onClose}
          />
        ),
      });
    }
  };

  const { setOauthPopup } = useOAuthPopup({
    onSuccess: inviteToken ? handleOAuthSuccess : undefined,
  });

  const onSubmit = async (values) => {
    try {
      const phoneNumber = values.phone ?? null;
      if (inviteToken) {
        const accountData = {
          email: invitedAccount.email,
          password: values.plainPassword,
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: phoneNumber,
        };

        attachSignupAnalyticsFromSearchParams(accountData, queryParams);

        if (!isTeamMemberInvitation) {
          accountData.company_name = values.companyName;
        }

        const response = await publicAxios.post(
          `/accounts/complete_invitation`,
          {
            account: accountData,
            invitation_token: inviteToken,
            planToken: planToken,
          }
        );

        reset();

        if (response.data.requires_confirmation) {
          setShowConfirmationModal(true);
        } else {
          toast({
            render: (props) => (
              <CustomToast
                status="success"
                title="Account created successfully! You can now log in."
                description=""
                onClose={props.onClose}
              />
            ),
          });
          navigate(`/login${location.search}`, { replace: true });
        }
      } else {
        const accountData = {
          email: values.email,
          password: values.plainPassword,
          first_name: values.firstName,
          last_name: values.lastName,
          company_name: values.companyName,
          phone_number: phoneNumber,
        };
        attachSignupAnalyticsFromSearchParams(accountData, queryParams);

        const body = {
          account: accountData,
        };

        if (planToken) {
          body.planToken = planToken;
        }

        await publicAxios.post(`/accounts`, body);
        reset();
        setShowConfirmationModal(true);
      }
    } catch (onError) {
      let errors = onError.response?.data?.errors || [
        'An error occurred during registration',
      ];
      errors = [...new Set(errors)];

      errors.forEach((error) => {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title={error}
              description=""
              onClose={props.onClose}
            />
          ),
        });
      });
    }
  };

  // useEffect(() => {
  //   const fetchSubscriptionPlans = async () => {
  //     setPlansLoading(true);
  //     try {
  //       const response = await publicAxios.get(
  //         '/internal/v1/billing/subscription_plans'
  //       );
  //       setPlans(response.data);
  //       if (planToken) {
  //         const matchingPlan = response.data.find(
  //           (plan) => plan.stripeProductId === `prod_${planToken}`
  //         );
  //         if (matchingPlan) {
  //           const selectedPrice = findMatchingPrice(matchingPlan.prices);
  //           setSelectedPlan({
  //             ...matchingPlan,
  //             selectedPrice,
  //           });
  //           setPlansLoading(false);
  //           return;
  //         }
  //       } else {
  //         setPlansLoading(false);
  //         return;
  //       }

  //       if (response.data.length === 1) {
  //         const plan = response.data[0];
  //         const selectedPrice = findMatchingPrice(plan.prices);
  //         setSelectedPlan({
  //           ...plan,
  //           selectedPrice,
  //         });
  //         setPlansLoading(false);
  //       }
  //     } catch (err) {
  //       setError(err);
  //       setPlansLoading(false);
  //     } finally {
  //       setPlansLoading(false);
  //     }
  //   };

  //   fetchSubscriptionPlans();
  // }, [planToken]);

  useEffect(() => {
    const fetchInvitedAccount = async () => {
      if (inviteToken) {
        try {
          setLoading(true);
          const response = await publicAxios.get(
            `/accounts/invitation/details`,
            { params: { invitation_token: inviteToken } }
          );

          if (!response.data.account) {
            throw new Error('Invalid invitation');
          }

          setInvitedAccount(response.data.account);
          setIsTeamMemberInvitation(response.data.type === 'team_member');
          if (response.data.organisation_name) {
            setOrganisationName(response.data.organisation_name);
          }
          reset({
            email: response.data.account.email,
            companyName: response.data.account.company_name,
          });
          setLoading(false);
        } catch (error) {
          console.error('Failed to fetch invitation details:', error);
          setInvitedAccount(null);
          setLoading(false);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchInvitedAccount();
  }, [inviteToken, publicAxios, reset]);

  // useEffect(() => {
  //   if (planToken) {
  //     setViewPlans(false);
  //   }
  // }, [planToken]);

  if (inviteToken && !invitedAccount && !loading) {
    return (
      <>
        <Box p={8}>
          <Link isExternal href={withTrackingParams('https://passentry.com')}>
            <Image src={logoSrcDark} alt="logo" w="10vw" />
          </Link>
        </Box>
        <Center pt={FORM_PADDING_TOP} p="10px">
          <CardForm>
            <VStack spacing={5} alignItems="center" textAlign="center">
              <Heading
                textStyle="headingFamilySemiBold"
                fontSize="22px"
                color="orangePrimary"
              >
                Invalid invitation
              </Heading>
              <Text
                textStyle="bodyFamilyRegular"
                fontSize="16px"
                color="#465467"
              >
                This invitation link has expired or is no longer valid. Please
                contact the person who invited you to request a new invitation.
              </Text>
            </VStack>
          </CardForm>
        </Center>
      </>
    );
  }

  return (
    <>
      {loading ? (
        <Center h="100vh">
          <Spinner size="xl" />
        </Center>
      ) : (
        <>
          {/* {viewPlans && !isTeamMemberInvitation ? (
            <SubscriptionPlans
              plans={plans}
              onSelectPlan={handleSelectPlan}
              selectedPlanId={selectedPlan?.id}
              selectedPlan={selectedPlan}
              isLoading={plansLoading}
              setViewPlans={setViewPlans}
              error={error}
            />
          ) : ( */}
          <Grid
            templateColumns={['1fr', '1fr', '1fr 1fr']}
            h="100%"
            minH="100vh"
            w="100vw"
          >
            {/* <PlanDetailsPanel
              selectedPlan={selectedPlan}
              // setViewPlans={setViewPlans}
              isTeamMemberInvitation={isTeamMemberInvitation}
            /> */}
            <TrialDetails isTeamMemberInvitation={isTeamMemberInvitation} />

            <Container
              bg="#F1F1F1"
              h="100%"
              minH="100vh"
              w={['100vw', '100vw', '100vw', '60vw']}
              px="10vw"
              pt="5vh"
              overflow="scroll"
            >
              <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <VStack alignItems="flex-start" w="100%">
                  <Text
                    textStyle="headingFamilySemiBold"
                    fontSize={['32px', '48px']}
                    color="#252525"
                  >
                    {inviteToken ? 'Complete Your Registration' : 'Sign up now'}
                  </Text>
                  <Text fontSize={['14px', '16px']} color="#252525">
                    Already have an account?{' '}
                    <Link
                      href={`/login${location.search}`}
                      textDecoration="underline"
                    >
                      Log in
                    </Link>
                  </Text>
                  {isTeamMemberInvitation && organisationName && (
                    <Text color="orangePrimary" textStyle="bodyRegular" mb={2}>
                      You're joining {organisationName} as a team member
                    </Text>
                  )}

                  <Flex direction="column" gap={3} w="100%" mt={5}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      <GridItem colSpan={{ base: 1, lg: 2, xl: 1 }}>
                        <FormControl isInvalid={errors.firstName}>
                          <FormLabel color="#4C4C4C">
                            First name
                            <Box as="span" color="error">
                              *
                            </Box>
                          </FormLabel>
                          <Input
                            placeholder="Your name"
                            {...register('firstName')}
                            bg="white"
                            borderRadius="12px"
                            border="1px solid #D0D5DD"
                          />
                          <FormErrorMessage mt={2} ml={2}>
                            {errors.firstName && errors.firstName.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={{ base: 1, lg: 2, xl: 1 }}>
                        <FormControl isInvalid={errors.lastName}>
                          <FormLabel color="#4C4C4C">
                            Last name
                            <Box as="span" color="error">
                              *
                            </Box>
                          </FormLabel>
                          <Input
                            placeholder="Your last name"
                            {...register('lastName')}
                            bg="white"
                            borderRadius="12px"
                            border="1px solid #D0D5DD"
                          />
                          <FormErrorMessage mt={2} ml={2}>
                            {errors.lastName && errors.lastName.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>
                      {!isTeamMemberInvitation && (
                        <GridItem colSpan={{ base: 1, md: 2 }}>
                          <FormControl isInvalid={errors.companyName}>
                            <FormLabel color="#4C4C4C">
                              Company name
                              <Box as="span" color="error">
                                *
                              </Box>
                            </FormLabel>
                            <Input
                              placeholder="Enter company name"
                              {...register('companyName')}
                              bg="white"
                              borderRadius="12px"
                              border="1px solid #D0D5DD"
                            />
                            <FormErrorMessage mt={2} ml={2}>
                              {errors.companyName && errors.companyName.message}
                            </FormErrorMessage>
                          </FormControl>
                        </GridItem>
                      )}
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <FormControl isInvalid={errors.email} isRequired>
                          <FormLabel color="#4C4C4C">Email</FormLabel>
                          <Input
                            type="email"
                            {...register('email', {
                              required: 'Email is required',
                              pattern: {
                                value:
                                  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Invalid email address',
                              },
                            })}
                            isReadOnly={!!inviteToken}
                            bg={inviteToken ? 'gray.50' : 'white'}
                            value={
                              inviteToken ? invitedAccount?.email : undefined
                            }
                            borderRadius="12px"
                            border="1px solid #D0D5DD"
                            placeholder="Enter email"
                            disabled={invitedAccount}
                          />
                          <FormErrorMessage mt={2} ml={2}>
                            {errors.email && errors.email.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <FormControl isInvalid={!!errors.phone}>
                          <FormLabel color="#4C4C4C">Phone number</FormLabel>
                          <Controller
                            name="phone"
                            control={control}
                            defaultValue={null}
                            render={({ field: { value, onChange } }) => (
                              <Box
                                w="100%"
                                bg="white"
                                borderRadius="12px"
                                border="1px solid #D0D5DD"
                                px="20px"
                                _focus={{
                                  border: '2px solid #2F343D',
                                  backgroundColor: 'white',
                                  color: 'secondaryDark',
                                }}
                                _hover={{
                                  background: 'rgba(221, 221, 221, 0.5)',
                                  border: '2px solid #2F343D',
                                  color: 'secondaryDark',
                                }}
                              >
                                <PhoneInput
                                  international
                                  defaultCountry="GB"
                                  value={value ?? undefined}
                                  onChange={onChange}
                                  inputComponent={ChakraPhoneInputField}
                                  inputProps={{
                                    id: 'phone',
                                    placeholder: 'Your phone number',
                                    autoComplete: 'tel',
                                  }}
                                />
                              </Box>
                            )}
                          />
                          <FormErrorMessage mt={2} ml={2}>
                            {errors.phone?.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl
                          isRequired
                          isInvalid={errors.plainPassword}
                        >
                          <FormLabel color="#4C4C4C">Password</FormLabel>
                          <InputGroup>
                            <Input
                              id="plainPassword"
                              type={show ? 'text' : 'password'}
                              placeholder="Enter password"
                              {...register('plainPassword')}
                              bg="white"
                              borderRadius="12px"
                              border="1px solid #D0D5DD"
                              autoComplete="new-password"
                            />
                            <InputRightElement>
                              {show ? (
                                <CustomVisibilityOffIcon
                                  onClick={showPasswordClickHandler}
                                  boxSize="18px"
                                  cursor="pointer"
                                  color={modeIconColor}
                                />
                              ) : (
                                <CustomVisibilityIcon
                                  onClick={showPasswordClickHandler}
                                  boxSize="18px"
                                  cursor="pointer"
                                  color={modeIconColor}
                                />
                              )}
                            </InputRightElement>
                          </InputGroup>
                          {fieldStatePassword.invalid && (
                            <FormHelperText mt={2} ml={2}>
                              <VStack spacing={1} alignItems="left">
                                <HStack spacing={10} alignItems="left">
                                  <Box
                                    color={
                                      lengthInBetween(watchPassword, 6, 30)
                                        ? 'success'
                                        : '#989898'
                                    }
                                  >
                                    {lengthInBetween(watchPassword, 6, 30) ? (
                                      <CustomCheckIcon boxSize={2} mr={1} />
                                    ) : (
                                      <CustomExclamationMarkIcon
                                        boxSize={2}
                                        mr={1}
                                      />
                                    )}
                                    Between 6 and 30 characters
                                  </Box>
                                  <Box
                                    color={
                                      hasLowerAndUpperCase(watchPassword)
                                        ? 'success'
                                        : '#989898'
                                    }
                                  >
                                    {hasLowerAndUpperCase(
                                      watchPassword,
                                      6,
                                      30
                                    ) ? (
                                      <CustomCheckIcon boxSize={2} mr={1} />
                                    ) : (
                                      <CustomExclamationMarkIcon
                                        boxSize={2}
                                        mr={1}
                                      />
                                    )}
                                    Both lower and upper case letters
                                  </Box>
                                </HStack>
                                <Box
                                  color={
                                    hasDigit(watchPassword) &&
                                    hasSymbol(watchPassword)
                                      ? 'success'
                                      : '#989898'
                                  }
                                >
                                  {hasDigit(watchPassword) &&
                                  hasSymbol(watchPassword) ? (
                                    <CustomCheckIcon boxSize={2} mr={1} />
                                  ) : (
                                    <CustomExclamationMarkIcon
                                      boxSize={2}
                                      mr={1}
                                    />
                                  )}
                                  At least one number and a symbol
                                </Box>
                              </VStack>
                            </FormHelperText>
                          )}
                        </FormControl>
                      </GridItem>
                      <FormControl
                        isRequired
                        isInvalid={errors.passwordConfirmation}
                      >
                        <FormLabel color="#4C4C4C">
                          Password confirmation
                        </FormLabel>
                        <Input
                          id="passwordConfirmation"
                          type={show ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          {...register('passwordConfirmation')}
                          bg="white"
                          borderRadius="12px"
                          border="1px solid #D0D5DD"
                          autoComplete="new-password"
                        />
                        <Box
                          color="#989898"
                          mt={2}
                          ml={2}
                          fontSize="12px"
                          alignContent="center"
                        >
                          {errors.passwordConfirmation && (
                            <CustomExclamationMarkIcon boxSize={2} mr={1} />
                          )}
                          {errors.passwordConfirmation?.message ||
                            errors.passwordConfirmation}
                        </Box>
                      </FormControl>
                    </SimpleGrid>
                    <FormControl
                      display="flex"
                      alignItems="center"
                      mt={3}
                      gap={2}
                    >
                      <Checkbox
                        {...register('termsAndCond')}
                        variant="primary"
                        defaultChecked={false}
                        isChecked={watchTermsAndCond}
                      />
                      <FormLabel mb="0">
                        <Text fontSize={['14px', '16px']} color="#252525">
                          <Text as="span" textStyle="bodyFamilyRegular">
                            I agree to the
                          </Text>{' '}
                          <Link
                            href="https://passentry.com/terms-of-service"
                            color="orangePrimary"
                            fontSize="16px"
                            textDecoration="underline"
                            isExternal
                          >
                            Terms of Service
                          </Link>
                          <Text as="span" textStyle="bodyFamilyRegular">
                            {' '}
                            and{' '}
                          </Text>
                          <Link
                            href="https://passentry.com/privacy-policy/"
                            color="orangePrimary"
                            fontSize="16px"
                            textDecoration="underline"
                            isExternal
                          >
                            Privacy Policy.
                          </Link>
                        </Text>
                      </FormLabel>
                    </FormControl>
                    {/* <FormControl
                        display="flex"
                        alignItems="center"
                        mt={2}
                        gap={2}
                      >
                        <Checkbox
                          {...register('newsletter')}
                          variant="primary"
                          defaultChecked={false}
                          isChecked={watchNewsletter}
                        />
                        <FormLabel mb="0">
                          <Text
                            fontSize={['14px', '16px']}
                            color="#252525"
                            textStyle="bodyFamilyRegular"
                          >
                            I want to receive emails about the product and
                            feature updates
                          </Text>
                        </FormLabel>
                      </FormControl> */}
                  </Flex>

                  {CARDLESS_FREE_TRIAL_ENABLED && !inviteToken ? (
                    <Box
                      w="full"
                      mt={4}
                      p={4}
                      bg="white"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor="orangePrimary"
                    >
                      <Text fontSize="14px" color="#465467" mb={4}>
                        Get 14 days of trial access - no card needed. After 14
                        days, your account will be locked unless you upgrade to
                        a paid plan. You will not be charged automatically.
                      </Text>
                      <Button
                        w="full"
                        type="submit"
                        isLoading={isSubmitting}
                        disabled={!isDirty || !isValid || isSubmitting}
                        alt="Submit"
                        variant="gradient"
                        _hover={{
                          opacity: 0.8,
                        }}
                      >
                        Start your free trial
                      </Button>
                    </Box>
                  ) : (
                    <Box w="full">
                      <Button
                        w="full"
                        type="submit"
                        isLoading={isSubmitting}
                        disabled={!isDirty || !isValid || isSubmitting}
                        mt={6}
                        alt="Submit"
                        variant="gradient"
                        _hover={{
                          opacity: 0.8,
                        }}
                      >
                        Create account
                      </Button>
                    </Box>
                  )}
                  {inviteToken && (
                    <>
                      <Flex align="center" w="full" my={4}>
                        <Divider borderColor="#D0D5DD" />
                        <Text
                          px={4}
                          textStyle="bodySmall"
                          color="#666666"
                          whiteSpace="nowrap"
                        >
                          Or
                        </Text>
                        <Divider borderColor="#D0D5DD" />
                      </Flex>
                      <Box w="full">
                        <SsoOptions setOauthPopup={setOauthPopup} />
                      </Box>
                    </>
                  )}
                  {/* <Flex justifyContent="center" w="100%" pb={8}>
                    <Text
                      fontSize={['12px', '14px']}
                      color="#252525"
                      textAlign="center"
                    >
                      By creating an account, you agree to the{' '}
                      <Link
                        href="https://passentry.com/terms-and-conditions/"
                        textDecoration="underline"
                      >
                        Terms of use
                      </Link>{' '}
                      and{' '}
                      <Link
                        href="https://passentry.com/privacy-policy/"
                        textDecoration="underline"
                      >
                        Privacy Policy
                      </Link>{' '}
                    </Text>
                  </Flex> */}
                </VStack>
              </form>
            </Container>
            <EmailConfirmationModal
              isOpen={showConfirmationModal}
              onClose={() => {
                setShowConfirmationModal(false);
                navigate(`/login${location.search}`, { replace: true });
              }}
            />
          </Grid>
          {/* )} */}
        </>
      )}
    </>
  );
};

export default SignUpPage;
